/**
 * FreshnessBadge — Shows data age as green/yellow/red indicator.
 *
 * Green:  < 24 hours (fresh)
 * Yellow: 1-7 days   (recent)
 * Red:    > 7 days   (stale, with warning)
 *
 * Two modes:
 * - compact: small pill for SourceDrawer cards
 * - full: larger badge below chat messages with freshness summary
 */

import React from 'react';
import { C } from '../design-system/primitives/constants';

function getFreshnessStatus(dateStr) {
  if (!dateStr) return null;
  const now = new Date();
  const date = new Date(dateStr);
  const daysSince = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (daysSince < 1) return { status: 'fresh', color: 'green', label: 'Live', daysSince };
  if (daysSince <= 7) return { status: 'recent', color: 'yellow', label: `${daysSince}d ago`, daysSince };
  return { status: 'stale', color: 'red', label: `${daysSince}d old`, daysSince };
}

// Earned color: green=fresh, gold=recent, red=stale
const COLOR_MAP = {
  green: { fg: C.elite, bg: `${C.elite}20`, border: `${C.elite}40` },
  yellow: { fg: C.gold, bg: `${C.gold}20`, border: `${C.gold}40` },
  red: { fg: C.dev, bg: `${C.dev}20`, border: `${C.dev}40` },
};

export function FreshnessBadge({ date, compact = false }) {
  const info = getFreshnessStatus(date);
  if (!info) return null;

  const colors = COLOR_MAP[info.color];

  if (compact) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold"
        style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.fg }}
      >
        <span className="w-1 h-1 rounded-full" style={{ background: colors.fg }} />
        {info.label}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-[10px] font-medium"
      style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.fg }}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${info.status === 'fresh' ? 'animate-pulse' : ''}`} style={{ background: colors.fg }} />
      Data: {info.label}
      {info.status === 'stale' && <span className="opacity-70">— may be outdated</span>}
    </span>
  );
}

/**
 * FreshnessSummary — Shows freshness for all datasets in a response.
 * Intended for display below chat messages.
 */
export function FreshnessSummary({ dataFreshness }) {
  if (!dataFreshness || typeof dataFreshness !== 'object') return null;

  const entries = Object.entries(dataFreshness).filter(
    ([, v]) => v && typeof v === 'object' && (v.latest || v.latest_game)
  );
  if (entries.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 mt-2 pt-2" style={{ borderTop: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 10, fontWeight: 500, color: C.t3 }}>
        Data freshness:
      </span>
      {entries.map(([key, val]) => (
        <span key={key} className="flex items-center gap-1">
          <code style={{ fontSize: 9, color: C.t3 }}>
            {key}
          </code>
          <FreshnessBadge date={val.latest || val.latest_game} compact />
        </span>
      ))}
    </div>
  );
}

export default FreshnessBadge;
